import React from "react";
import { ArrowLeft, FileText } from "lucide-react";
import { Button } from "../components/ui/button";

const HEADER_LOGO_URL = "/header_logo.jpeg";

export default function TermsAndConditions() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src={HEADER_LOGO_URL} alt="Kavach Logo" className="h-14 w-14 object-contain rounded-full" />
            <div>
              <h1 className="text-xl font-bold" style={{color: '#003366'}}>KAVACH INFRA SOLUTIONS LLP</h1>
              <p className="text-xs" style={{color: '#D4AF37', fontWeight: 600}}>रक्षा | सुरक्षा | सजगता</p>
            </div>
          </div>
          <Button
            onClick={() => window.location.href = '/'}
            variant="outline"
            style={{borderColor: '#003366', color: '#003366'}}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-10 h-10" style={{color: '#003366'}} />
            <h1 className="text-4xl font-bold" style={{color: '#003366'}}>Terms and Conditions</h1>
          </div>
          <p className="text-slate-600">Last Updated: November 2, 2025</p>
        </div>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>1. Agreement to Terms</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("User", "you", or "your") and Kavach Infra Solutions LLP ("Kavach", "we", "us", or "our") governing your access to and use of www.kavachinfra.in website and Kavach FireSight Pro platform (collectively, the "Services").
            </p>
            <p className="text-slate-700 leading-relaxed">
              By accessing or using our Services, you acknowledge that you have read, understood and agree to be bound by these Terms and our Privacy Policy. If you do not agree with these Terms, you must not access or use our Services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>2. Description of Services</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Kavach Infra Solutions LLP provides:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li><strong>Kavach FireSight Pro:</strong> An AI-powered platform for fire safety compliance analysis that generates NBC, NFPA and BIS compliant reports</li>
              <li><strong>Document Analysis:</strong> Automated analysis of construction drawings, floor plans and technical documents</li>
              <li><strong>BOQ Generation:</strong> Automated Bill of Quantities creation for fire safety equipment</li>
              <li><strong>AI Consultation:</strong> AI-powered chatbot for fire safety queries and guidance</li>
              <li><strong>Compliance Reports:</strong> Risk assessment and compliance scoring based on industry standards</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4 p-4 bg-amber-50 border-l-4 rounded" style={{borderColor: '#D4AF37'}}>
              <strong>Important:</strong> Our AI-generated reports are provided as guidance tools. All reports should be reviewed and validated by qualified fire safety professionals and engineers before implementation.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>3. User Registration and Accounts</h2>
            
            <h3 className="text-xl font-semibold mb-3" style={{color: '#003366'}}>3.1 Account Creation</h3>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>You must register for an account using Google OAuth authentication</li>
              <li>You must provide accurate and complete information</li>
              <li>You are responsible for maintaining the confidentiality of your account</li>
              <li>You must be at least 18 years of age to use our Services</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3" style={{color: '#003366'}}>3.2 Admin Approval</h3>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>All new user accounts require approval from our admin team</li>
              <li>We reserve the right to approve or reject any registration at our sole discretion</li>
              <li>Approval may take up to 24-48 hours</li>
              <li>You will be notified via email regarding your approval status</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3" style={{color: '#003366'}}>3.3 Account Security</h3>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>You are solely responsible for all activities that occur under your account</li>
              <li>You must notify us immediately of any unauthorized access or security breach</li>
              <li>We are not liable for any loss or damage arising from unauthorized use of your account</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>4. Acceptable Use</h2>
            
            <h3 className="text-xl font-semibold mb-3" style={{color: '#003366'}}>4.1 You Agree To:</h3>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>Use the Services only for lawful purposes and in accordance with these Terms</li>
              <li>Provide accurate and truthful information in all interactions</li>
              <li>Upload only documents and files you own or have permission to use</li>
              <li>Respect intellectual property rights of Kavach and third parties</li>
              <li>Maintain confidentiality of any sensitive information accessed through the Services</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3" style={{color: '#003366'}}>4.2 You Agree NOT To:</h3>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>Use the Services for any illegal or unauthorized purpose</li>
              <li>Upload malicious code, viruses or any harmful content</li>
              <li>Attempt to gain unauthorized access to any part of the Services</li>
              <li>Reverse engineer, decompile or disassemble any part of the Services</li>
              <li>Use automated systems or bots to access the Services</li>
              <li>Resell, redistribute or create derivative works from our Services</li>
              <li>Upload confidential or proprietary information of third parties without authorization</li>
              <li>Interfere with or disrupt the Services or servers</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>5. Intellectual Property Rights</h2>
            
            <h3 className="text-xl font-semibold mb-3" style={{color: '#003366'}}>5.1 Our Intellectual Property</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              All content, features, functionality, software, code, algorithms, AI models, design, graphics, logos, trademarks and service marks on our platform are owned by Kavach Infra Solutions LLP and are protected by Indian and international copyright, trademark, patent and other intellectual property laws.
            </p>

            <h3 className="text-xl font-semibold mb-3" style={{color: '#003366'}}>5.2 Your Content</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              You retain all ownership rights to the documents and files you upload. By uploading content, you grant us a limited, worldwide, non-exclusive license to:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>Process and analyze your documents using AI technology</li>
              <li>Store your content on our servers for the purpose of providing Services</li>
              <li>Generate reports and analysis based on your uploaded content</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              We will not use your uploaded content for any purpose other than providing the Services unless you give us explicit consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>6. AI-Generated Content and Disclaimers</h2>
            
            <h3 className="text-xl font-semibold mb-3" style={{color: '#003366'}}>6.1 AI Technology</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Kavach FireSight Pro uses advanced AI vision technology and machine learning models to analyze documents and generate compliance reports. While we strive for accuracy:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>AI-generated content may contain errors or inaccuracies</li>
              <li>Reports are provided as guidance and should not be solely relied upon</li>
              <li>All reports must be reviewed by qualified professionals</li>
              <li>We do not guarantee the accuracy, completeness or reliability of AI outputs</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3" style={{color: '#003366'}}>6.2 Professional Advice Disclaimer</h3>
            <p className="text-slate-700 leading-relaxed p-4 bg-red-50 border-l-4 border-red-500 rounded">
              <strong>IMPORTANT:</strong> The Services are not a substitute for professional fire safety engineering advice. You should always consult with licensed fire safety engineers, architects and relevant authorities before implementing any fire safety systems or making compliance decisions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>7. Fees and Payment</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Current services may be offered free of charge or on a subscription basis. We reserve the right to:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>Introduce fees for Services currently offered for free</li>
              <li>Modify pricing structures with 30 days prior notice</li>
              <li>Offer different pricing tiers for different features</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              All fees are non-refundable unless otherwise specified in writing.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>8. Limitation of Liability</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>KAVACH SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES</li>
              <li>OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU (IF ANY) IN THE 12 MONTHS PRECEDING THE CLAIM</li>
              <li>WE ARE NOT LIABLE FOR ANY LOSS OF DATA, PROFITS, BUSINESS OPPORTUNITIES OR REPUTATION</li>
              <li>WE ARE NOT LIABLE FOR ANY ERRORS IN AI-GENERATED CONTENT OR COMPLIANCE REPORTS</li>
              <li>WE ARE NOT LIABLE FOR IMPLEMENTATION OF FIRE SAFETY SYSTEMS BASED ON OUR REPORTS</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>9. Indemnification</h2>
            <p className="text-slate-700 leading-relaxed">
              You agree to indemnify, defend and hold harmless Kavach Infra Solutions LLP, its officers, directors, employees and agents from any claims, damages, losses, liabilities, costs and expenses (including legal fees) arising from:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-4">
              <li>Your use or misuse of the Services</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of third parties</li>
              <li>Implementation of fire safety systems based on our reports without professional validation</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>10. Service Availability and Modifications</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We reserve the right to:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>Modify, suspend or discontinue any part of the Services at any time</li>
              <li>Perform scheduled or emergency maintenance</li>
              <li>Limit features or access for certain users</li>
              <li>Update our AI models and algorithms</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              We are not liable for any modification, suspension or discontinuation of the Services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>11. Termination</h2>
            
            <h3 className="text-xl font-semibold mb-3" style={{color: '#003366'}}>11.1 By You</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              You may terminate your account at any time by contacting us at info@kavachinfra.in.
            </p>

            <h3 className="text-xl font-semibold mb-3" style={{color: '#003366'}}>11.2 By Us</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We may suspend or terminate your access immediately, without prior notice, for:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>Violation of these Terms</li>
              <li>Suspected fraudulent or illegal activity</li>
              <li>Non-payment of fees (if applicable)</li>
              <li>Any reason at our sole discretion</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3" style={{color: '#003366'}}>11.3 Effect of Termination</h3>
            <p className="text-slate-700 leading-relaxed">
              Upon termination, your right to use the Services will immediately cease. We may delete your account and data. Provisions regarding intellectual property, limitation of liability and indemnification shall survive termination.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>12. Governing Law and Dispute Resolution</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              These Terms shall be governed by and construed in accordance with the laws of India, without regard to conflict of law principles.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              Any dispute arising out of or relating to these Terms shall be subject to the exclusive jurisdiction of the courts in Pune, Maharashtra, India.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Before initiating any legal proceedings, you agree to first attempt to resolve disputes through good faith negotiations by contacting us at info@kavachinfra.in.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>13. Changes to Terms</h2>
            <p className="text-slate-700 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify users of material changes by posting the updated Terms on our website and updating the "Last Updated" date. Your continued use of the Services after such changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>14. Severability</h2>
            <p className="text-slate-700 leading-relaxed">
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force and effect.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>15. Entire Agreement</h2>
            <p className="text-slate-700 leading-relaxed">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and Kavach Infra Solutions LLP regarding the use of our Services and supersede all prior agreements and understandings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>16. Contact Information</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              For questions, concerns or notices regarding these Terms, please contact us:
            </p>
            <div className="bg-slate-50 p-6 rounded-lg border-l-4" style={{borderColor: '#D4AF37'}}>
              <p className="font-semibold mb-2" style={{color: '#003366'}}>Kavach Infra Solutions LLP</p>
              <p className="text-slate-700 mb-1"><strong>Email:</strong> info@kavachinfra.in</p>
              <p className="text-slate-700 mb-1"><strong>Phone:</strong> +91 9890838368 | +91 9593843999</p>
              <p className="text-slate-700 mb-1"><strong>Corporate Office:</strong></p>
              <p className="text-slate-700 pl-4">1304, 13th Floor, A Wing, Dev Corpora,</p>
              <p className="text-slate-700 pl-4">Eastern Express Highway, Opp. Cadbury Junction,</p>
              <p className="text-slate-700 pl-4">Thane West, Maharashtra 400601, India</p>
            </div>
          </section>

          <div className="mt-8 p-6 bg-blue-50 border rounded-lg">
            <p className="text-slate-700 text-sm leading-relaxed">
              By clicking "I Agree" or by accessing and using the Services, you acknowledge that you have read, understood and agree to be bound by these Terms and Conditions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
